import type {
  BadgeElement,
  BadgeLayoutMode,
  BadgeMergeTagElement,
  BadgePreset,
  BadgePrintData,
  BadgeTextTransform,
  BadgeShapeElement,
  BadgeTemplate,
  BadgeTextElement,
} from '@/types/badgeDesigner'

export const BADGE_DESIGN_DPI = 100
export const DEFAULT_BADGE_PRINT_DPI = 300

export const BADGE_FONT_FAMILIES = [
  'Arial',
  'Helvetica',
  'Tahoma',
  'Verdana',
  'Trebuchet MS',
  'Times New Roman',
  'Georgia',
  'Courier New',
] as const

const DEFAULT_TEXT_STYLE = {
  font_family: 'Arial',
  font_size: 40,
  font_weight: '600',
  text_align: 'left' as const,
  text_transform: 'none' as const,
  color: '#111827',
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const toNumber = (value: unknown, fallback: number, min = 0): number => {
  const num = Number(value)
  if (!Number.isFinite(num)) return fallback
  return Math.max(min, num)
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

export const createElementId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`

export const createDefaultBadgeTemplate = (): BadgeTemplate => ({
  preset: '4x6_single',
  width_in: 4,
  height_in: 6,
  print_dpi: DEFAULT_BADGE_PRINT_DPI,
  layout_mode: 'single_sided',
  front_elements: [
    {
      id: createElementId(),
      type: 'merge_tag',
      merge_key: 'full_name',
      x: 28,
      y: 32,
      width: 340,
      height: 90,
      ...DEFAULT_TEXT_STYLE,
      font_size: 52,
      font_weight: '700',
    } satisfies BadgeMergeTagElement,
    {
      id: createElementId(),
      type: 'merge_tag',
      merge_key: 'email',
      x: 28,
      y: 130,
      width: 340,
      height: 55,
      ...DEFAULT_TEXT_STYLE,
      font_size: 30,
      font_weight: '500',
    } satisfies BadgeMergeTagElement,
    {
      id: createElementId(),
      type: 'qr_code',
      x: 246,
      y: 430,
      width: 122,
      height: 122,
    },
  ],
  back_elements: [],
})

const normalizeTextAlign = (value: unknown): 'left' | 'center' | 'right' => {
  if (value === 'center' || value === 'right' || value === 'left') return value
  return 'left'
}

const normalizeTextTransform = (value: unknown): BadgeTextTransform => {
  if (value === 'uppercase' || value === 'capitalize' || value === 'none') return value
  return 'none'
}

const normalizeShapeKind = (value: unknown): BadgeShapeElement['shape_kind'] => {
  if (value === 'ellipse' || value === 'line' || value === 'rectangle') return value
  return 'rectangle'
}

const normalizeElement = (raw: unknown): BadgeElement | null => {
  if (!isObject(raw)) return null
  const type = String(raw.type || '')
  const id = String(raw.id || createElementId())
  const base = {
    id,
    x: toNumber(raw.x, 0),
    y: toNumber(raw.y, 0),
    width: toNumber(raw.width, 120, 1),
    height: toNumber(raw.height, 60, 1),
    z_index: Number.isFinite(Number(raw.z_index)) ? Number(raw.z_index) : undefined,
  }

  if (type === 'text') {
    return {
      ...base,
      type: 'text',
      text: String(raw.text ?? 'Text'),
      font_family: String(raw.font_family || DEFAULT_TEXT_STYLE.font_family),
      font_size: toNumber(raw.font_size, DEFAULT_TEXT_STYLE.font_size, 6),
      font_weight: String(raw.font_weight || DEFAULT_TEXT_STYLE.font_weight),
      text_align: normalizeTextAlign(raw.text_align),
      text_transform: normalizeTextTransform(raw.text_transform),
      color: String(raw.color || DEFAULT_TEXT_STYLE.color),
    }
  }

  if (type === 'merge_tag') {
    return {
      ...base,
      type: 'merge_tag',
      merge_key: String(raw.merge_key || 'full_name'),
      font_family: String(raw.font_family || DEFAULT_TEXT_STYLE.font_family),
      font_size: toNumber(raw.font_size, DEFAULT_TEXT_STYLE.font_size, 6),
      font_weight: String(raw.font_weight || DEFAULT_TEXT_STYLE.font_weight),
      text_align: normalizeTextAlign(raw.text_align),
      text_transform: normalizeTextTransform(raw.text_transform),
      color: String(raw.color || DEFAULT_TEXT_STYLE.color),
    }
  }

  if (type === 'qr_code') {
    return {
      ...base,
      type: 'qr_code',
      width: toNumber(raw.width, 120, 8),
      height: toNumber(raw.height, 120, 8),
    }
  }

  if (type === 'image') {
    return {
      ...base,
      type: 'image',
      src: String(raw.src || ''),
    }
  }

  if (type === 'shape') {
    return {
      ...base,
      type: 'shape',
      shape_kind: normalizeShapeKind(raw.shape_kind),
      stroke_color: String(raw.stroke_color || '#111827'),
      fill_color: String(raw.fill_color || '#ffffff'),
      stroke_width: toNumber(raw.stroke_width, 2, 0),
    }
  }

  return null
}

const normalizePreset = (value: unknown): BadgePreset => {
  if (value === '4x6_single' || value === '4x6_double' || value === 'custom') return value
  return '4x6_single'
}

const normalizeLayoutMode = (value: unknown): BadgeLayoutMode =>
  value === 'double_sided' ? 'double_sided' : 'single_sided'

export const normalizeBadgeTemplate = (rawTemplate?: unknown): BadgeTemplate => {
  const fallback = createDefaultBadgeTemplate()
  if (!isObject(rawTemplate)) return fallback

  const preset = normalizePreset(rawTemplate.preset)
  let widthIn = clamp(toNumber(rawTemplate.width_in, fallback.width_in, 1), 1, 20)
  let heightIn = clamp(toNumber(rawTemplate.height_in, fallback.height_in, 1), 1, 20)
  const printDpi = clamp(toNumber(rawTemplate.print_dpi, fallback.print_dpi, 100), 100, 600)
  let layoutMode = normalizeLayoutMode(rawTemplate.layout_mode)

  if (preset === '4x6_single') {
    widthIn = 4
    heightIn = 6
    layoutMode = 'single_sided'
  } else if (preset === '4x6_double') {
    widthIn = 4
    heightIn = 6
    layoutMode = 'double_sided'
  }

  const frontElements = Array.isArray(rawTemplate.front_elements)
    ? rawTemplate.front_elements.map(normalizeElement).filter(Boolean) as BadgeElement[]
    : fallback.front_elements
  const backElements = Array.isArray(rawTemplate.back_elements)
    ? rawTemplate.back_elements.map(normalizeElement).filter(Boolean) as BadgeElement[]
    : fallback.back_elements

  return {
    id: Number.isFinite(Number(rawTemplate.id)) ? Number(rawTemplate.id) : undefined,
    preset,
    width_in: widthIn,
    height_in: heightIn,
    print_dpi: printDpi,
    layout_mode: layoutMode,
    front_elements: frontElements,
    back_elements: layoutMode === 'double_sided' ? backElements : [],
  }
}

export const resolveMergeValue = (mergeKey: string, data?: BadgePrintData): string => {
  if (!mergeKey) return ''
  const registrant = data?.registrant
  const event = data?.event

  if (!registrant) return ''

  if (mergeKey.startsWith('event.')) {
    const key = mergeKey.replace(/^event\./, '')
    const value = event?.[key as keyof typeof event]
    return value == null ? '' : String(value)
  }

  const direct = registrant[mergeKey as keyof typeof registrant]
  if (direct != null && direct !== '') {
    return Array.isArray(direct) ? direct.join(', ') : String(direct)
  }

  const answers = registrant.answers || {}
  const answerValue = (answers as Record<string, unknown>)[mergeKey]
  if (answerValue == null) return ''
  if (Array.isArray(answerValue)) return answerValue.map((item) => String(item)).join(', ')
  if (isObject(answerValue)) return JSON.stringify(answerValue)
  return String(answerValue)
}

export const applyTextTransform = (
  text: string,
  textTransform: BadgeTextTransform = 'none'
): string => {
  if (textTransform === 'uppercase') return text.toUpperCase()
  if (textTransform === 'capitalize') return text.replace(/\b([a-z])/gi, (char) => char.toUpperCase())
  return text
}

export const resolveElementText = (
  element: BadgeTextElement | BadgeMergeTagElement,
  data?: BadgePrintData
): string => {
  const rawText = element.type === 'text' ? element.text : resolveMergeValue(element.merge_key, data)
  return applyTextTransform(rawText, element.text_transform)
}

export const badgeCanvasSizeInPixels = (
  template: Pick<BadgeTemplate, 'width_in' | 'height_in'>,
  dpi: number
) => ({
  width: Math.max(1, Math.round(template.width_in * dpi)),
  height: Math.max(1, Math.round(template.height_in * dpi)),
})

export const templateIsDoubleSided = (template: Pick<BadgeTemplate, 'layout_mode'>) =>
  template.layout_mode === 'double_sided'

export const ensureTemplatePresetConsistency = (template: BadgeTemplate): BadgeTemplate => {
  if (template.preset === '4x6_single') {
    return {
      ...template,
      width_in: 4,
      height_in: 6,
      layout_mode: 'single_sided',
      back_elements: [],
    }
  }

  if (template.preset === '4x6_double') {
    return {
      ...template,
      width_in: 4,
      height_in: 6,
      layout_mode: 'double_sided',
    }
  }

  return template
}
